<script setup lang="ts">
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

// eslint-disable-next-line
dayjs.extend(duration);

interface Props {
  tournament: TTournament;
}

const props = defineProps<Props>();

const currentTime = ref(dayjs());
const tournamentDetails = ref<TTournamentDetailed>();
const { loadTournamentInfo } = useSmarticoTournaments();
const { isAuthenticated } = useAuthStore();
const remainingTimes = ref<Record<number, string>>({});

const filterTournamentNameTitle = computed(() => {
  return props.tournament.name;
});

const prizeAmount = computed(() => {
  let prize = 'Misto';

  if (props.tournament.prize_pool_short) {
    prize = `${props.tournament.prize_pool_short}`;
  }

  return prize;
});

const tournamentImageUrl = computed(() => {
  if (import.meta.client && window.innerWidth < 768) {
    return props.tournament.image2_mobile || props.tournament.image2;
  }

  return props.tournament.image2 || props.tournament.image2_mobile;
});

async function loadTournamentDetails() {
  if (!props.tournament.instance_id) {
    console.error('Tournament instance ID not available');

    return undefined;
  }

  try {
    if (import.meta.client && window && window._smartico) {
      let details;
      const maxRetries = 10;

      const retryApiCall = async (
        apiCall: () => Promise<any>,
        maxRetries: number,
      ) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const result = await apiCall();

            if (result) {
              return result;
            }
          } catch (err) {
            console.warn(`Retry attempt ${attempt} failed:`, err);
            if (attempt === maxRetries) {
              throw err;
            }
            // Wait before retrying, increasing delay with each attempt
            await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
          }
        }
        throw new Error('API call failed after maximum retries');
      };

      if (isAuthenticated && props.tournament.is_user_registered) {
        details = await retryApiCall(
          () =>
            window._smartico.api.getTournamentInstanceInfo(
              props.tournament.instance_id,
            ),
          maxRetries,
        );

        if (details && details.instance_id !== props.tournament.instance_id) {
          details = await retryApiCall(
            () =>
              window._smartico.api.getTournamentInstanceInfo(
                props.tournament.instance_id,
              ),
            maxRetries,
          );
        }

        return details;
      } else {
        details = await retryApiCall(
          () =>
            window._smartico
              .vapi('EN')
              .getTournamentInstanceInfo(props.tournament.instance_id),
          maxRetries,
        );

        return details;
      }
    } else {
      const details = await loadTournamentInfo(props.tournament.instance_id);

      return details;
    }
  } catch (error) {
    console.error(
      `Error loading tournament details ${props.tournament.instance_id}:`,
      error,
    );

    return undefined;
  }
}

async function startTournamentDetailsLoading() {
  const details = await loadTournamentDetails();

  if (details) {
    tournamentDetails.value = details;
  }

  currentTime.value = dayjs();
}

function formatPlayerName(username: string): string {
  if (username.length <= 4) return username;

  return username.substring(0, 4) + '***';
}

function formatPrize(position: number): string {
  if (!tournamentDetails.value || !tournamentDetails.value.prizes)
    return 'R$0,00';

  const prize = tournamentDetails.value.prizes.find(
    (p: any) => position >= p.place_from && position <= p.place_to,
  );

  return prize ? prize.name : 'R$0,00';
}

function getTournamentStatus(tournament: TTournament): string {
  const statuses = [
    { condition: tournament.is_user_registered, label: 'VER RANKING' },
    { condition: tournament.is_finished, label: 'FINALIZADO' },
    { condition: !tournament.is_in_progress, label: 'EM BREVE' },
    { condition: true, label: 'PARTICIPAR' },
  ];

  return statuses.find((status) => status.condition)?.label || 'PARTICIPAR';
}

function formatTimeRemaining(end_time: number): string {
  const now = Date.now();
  let diff = end_time - now;

  if (diff <= 0) return '00:00:00:00';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  diff -= days * 1000 * 60 * 60 * 24;

  const hours = Math.floor(diff / (1000 * 60 * 60));

  diff -= hours * 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));

  diff -= minutes * 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  const pad = (n: number) => n.toString().padStart(2, '0');

  return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

const tryLoadTournamentDetails = (maxRetries = 5, delayMs = 1000) => {
  let retries = 0;

  const attempt = async (): Promise<void> => {
    if (window && window._smartico) {
      await startTournamentDetailsLoading();

      return;
    }

    if (retries < maxRetries) {
      retries++;
      console.log(`Aguardando _smartico, tentativa ${retries}/${maxRetries}`);
      await new Promise((resolve) => setTimeout(resolve, delayMs));

      return attempt();
    }

    console.warn('Falha ao carregar _smartico após tentativas máximas');
  };

  return attempt();
};

let intervalId: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
  tryLoadTournamentDetails();

  if (props.tournament?.end_time) {
    intervalId = setInterval(() => {
      if (props.tournament.end_time > Date.now()) {
        remainingTimes.value[props.tournament.instance_id] =
          formatTimeRemaining(props.tournament.end_time);
      }
    }, 1000);
  }
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = undefined;
  }
});
</script>

<template>
  <div
    class="bg-black/20 rounded-md text-left w-full md:w-auto select-none"
    :class="{
      'opacity-80': props.tournament.is_finished,
    }"
    draggable="false"
    style="
      min-height: 120px;
      display: flex;
      flex-direction: column;
      width: 100%;
    "
  >
    <div class="flex w-full h-auto flex-grow">
      <div
        class="relative w-[140px] h-[140px] sm:w-[160px] md:w-[210px] sm:h-[160px] md:h-[210px]"
      >
        <!-- Fundo com gradiente centralizado -->
        <div
          class="rounded-md absolute w-[calc(100%+3px)] h-[calc(100%+3px)]"
          style="
            background: linear-gradient(
              90deg,
              #ff3400 0%,
              rgba(255, 52, 0, 0) 100%
            );
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 1;
          "
        ></div>
        <!-- Imagem Torneio -->
        <img
          :alt="`Tournament-${props.tournament.tournament_id}`"
          class="rounded-md object-cover absolute w-[140px] h-[140px] md:w-[calc(100%)] md:h-[calc(100%)]"
          :class="{
            grayscale: !props.tournament.is_in_progress,
          }"
          draggable="false"
          :src="tournamentImageUrl"
          style="
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            display: block;
            z-index: 2;
          "
        />

        <!-- Sombra linear só embaixo -->
        <div
          class="absolute w-[calc(100%)] h-[calc(100%)]"
          style="
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            pointer-events: none;
            border-radius: 0.375rem;
            overflow: hidden;
            z-index: 3;
          "
        >
          <div
            style="
              position: absolute;
              bottom: 0;
              left: 0;
              width: 100%;
              height: 40%;
              pointer-events: none;
              background: linear-gradient(
                to top,
                rgba(0, 0, 0, 1) 0%,
                rgba(0, 0, 0, 0.5) 86%,
                rgba(0, 0, 0, 0) 100%
              );
              border-radius: 0 0 0.375rem 0.375rem;
            "
          ></div>
        </div>
        <!-- Textos dentro da imagem -->
        <!-- Nome operadora -->
        <div
          class="text-[10px] leading-[12px] w-full text-white absolute top-[1px] md:top-[5px] z-10 text-center"
        >
          <span
            class="font-bold px-[5px] py-[3px] md:px-[10px] md:py-[6px] justify-center top-0 z-10 text-[8px] md:text-[9px] leading-[10px] rounded-b-lg uppercase"
            style="
              background: radial-gradient(
                136.67% 136.67% at 36.76% -16.67%,
                #ff704a 0%,
                #ff3400 100%
              );
            "
            >{{ props.tournament.ribbon }}</span
          >
        </div>
        <!-- Nome do Torneio e Prêmio -->
        <div
          class="text-[9px] md:text-[12px] leading-[11px] md:leading-[15px] w-full text-white absolute bottom-[10px] md:bottom-[15px] z-10 px-[7px] md:px-[15px] font-semibold"
          style="white-space: normal; overflow-wrap: break-word"
        >
          <span
            v-if="props.tournament?.registration_count"
            class="flex items-center mb-0.5"
          >
            <NuxtIcon
              class="text-[#00ff00] -mt-0.5 mr-1"
              name="users"
            />
            {{ props.tournament?.registration_count ?? '---' }}</span
          >
          <span class="font-extrabold md:text-[13px]">{{
            filterTournamentNameTitle
          }}</span
          ><br />
          <span>{{ 'Prêmio: ' + prizeAmount }}</span>
        </div>
      </div>

      <div
        class="flex-1 rounded-r-md bg-[linear-gradient(270deg,_rgba(255,255,255,0.05)_0%,_rgba(255,255,255,0)_100%)] flex flex-col p-0 pt-0 md:pt-1 min-h-full flex-grow max-w-[calc(100%-140px)] sm:max-w-[calc(100%-160px)] md:max-w-none px-2"
      >
        <table class="text-white text-xs md:text-lg w-full">
          <thead
            :class="{
              hidden:
                !props.tournament.is_in_progress && props.tournament.is_active,
            }"
          >
            <tr class="text-left text-[8px] md:text-[10px] leading-[10px]">
              <th class="w-[24px] md:w-[45px] text-center p-[5px] pl-[10px]"
                >#</th
              >
              <th class="px-[5px]">Jogador</th>
              <th class="w-[120px]">Prêmio</th>
            </tr>
          </thead>
          <tbody>
            <template
              v-if="
                tournamentDetails?.players &&
                tournamentDetails.players.length > 0
              "
            >
              <tr
                v-for="(player, playerIndex) in tournamentDetails.players.slice(
                  0,
                  3,
                )"
                :key="playerIndex"
                class="text-[8px] md:text-[12px] leading-[10px] md:font-semibold"
                :class="{
                  'bg-[#D6ED16] text-black': player.is_me,
                  'bg-[#FFFFFF1A]': playerIndex % 2 === 0 && !player.is_me,
                }"
              >
                <td
                  class="text-center rounded-l-md h-[22px] md:h-[32px] pl-[5px] w-[24px] md:w-[45px]"
                >
                  <div class="flex items-center justify-center h-full">
                    <NuxtImg
                      v-if="player.position"
                      :alt="`${player.position}º`"
                      class="w-[24px] md:w-[26px]"
                      draggable="false"
                      :src="
                        getAssetsUrlForCDN(
                          `/assets/gamification/t-place-${player.position}.png`,
                        )
                      "
                    />
                    <span
                      v-else
                      class="text-center text-[8px] md:text-[12px]"
                      >{{ player.position }}</span
                    >
                  </div>
                </td>
                <td
                  class="text-[8px] md:text-[12px] leading-[10px] py-[5px] px-[5px] h-[22px] align-middle whitespace-nowrap overflow-hidden text-ellipsis font-bold max-w-[50px]"
                  :title="player.public_username || '**********'"
                  >{{
                    formatPlayerName(player.public_username || '**********')
                  }}</td
                >
                <td
                  class="rounded-r-md text-[8px] md:text-[12px] leading-[10px] h-[22px] align-middle font-bold flex-1 whitespace-nowrap overflow-hidden text-ellipsis max-w-0"
                  >{{ formatPrize(player.position) }}</td
                >
              </tr>
              <tr
                v-for="index in 3 -
                (tournamentDetails.players.length > 3
                  ? 3
                  : tournamentDetails.players.length)"
                :key="'placeholder-' + index"
                class="text-[8px] md:text-[12px] leading-[10px] md:font-semibold"
                :class="{
                  'bg-[#FFFFFF1A]':
                    (tournamentDetails.players.length + index) % 2 === 0,
                }"
              >
                <td
                  class="text-center rounded-l-md h-[22px] md:h-[32px] pl-[5px] w-[24px] md:w-[45px]"
                >
                  <div class="flex items-center justify-center h-full">
                    <span class="text-center text-[8px] md:text-[12px]">-</span>
                  </div>
                </td>
                <td
                  class="text-[8px] md:text-[12px] leading-[10px] py-[5px] px-[5px] h-[22px] align-middle whitespace-nowrap overflow-hidden text-ellipsis font-bold"
                  >-</td
                >
                <td
                  class="rounded-r-md text-[8px] md:text-[12px] leading-[10px] h-[22px] align-middle font-bold flex-1"
                  >-</td
                >
              </tr>
            </template>
            <template v-else>
              <tr
                v-for="index in 3"
                :key="'empty-' + index"
                class="text-[8px] md:text-[12px] leading-[10px] md:font-semibold"
                :class="{
                  'bg-[#FFFFFF1A]': index % 2 === 0,
                }"
              >
                <td
                  class="text-center rounded-l-md h-[22px] md:h-[32px] pl-[5px] w-[24px] md:w-[45px]"
                >
                  <div class="flex items-center justify-center h-full">
                    <span class="text-center text-[8px] md:text-[12px]">-</span>
                  </div>
                </td>
                <td
                  class="text-[8px] md:text-[12px] leading-[10px] py-[5px] px-[5px] h-[22px] align-middle whitespace-nowrap overflow-hidden text-ellipsis font-bold"
                  >-</td
                >
                <td
                  class="rounded-r-md text-[8px] md:text-[12px] leading-[10px] h-[22px] align-middle font-bold flex-1"
                  >-</td
                >
              </tr>
            </template>
          </tbody>
          <tfoot
            class="mt-auto"
            style="width: 100%"
          >
            <tr style="width: 100%">
              <td
                colspan="3"
                style="width: 100%"
              >
                <div
                  class="flex md:flex-wrap items-center pt-[5px] md:pt-[10px] rounded-md gap-2 md:gap-3"
                  style="width: 100%"
                >
                  <div
                    class="w-5 h-5 p-[4px] md:w-8 md:h-8 md:p-[7px] self-center rounded-md bg-[#FFFFFF1A]"
                  >
                    <span style="width: 100%; height: 100%">
                      <NuxtIcon name="timer" />
                    </span>
                  </div>

                  <div
                    class="flex-1 flex flex-col md:flex-row md:gap-2 items-start md:items-center"
                  >
                    <div
                      class="text-[8px] md:text-[12px] leading-[10px] md:opacity-70 md:font-bold whitespace-nowrap"
                      >{{
                        props.tournament.is_in_progress
                          ? $t('gamification.tournament.ends_in')
                          : $t('gamification.tournament.starts_in')
                      }}</div
                    >
                    <div
                      class="text-[8px] md:text-[12px] leading-[10px] font-bold tracking-wide w-[60px]"
                      :class="{
                        'text-[#D6ED16]': !props.tournament.is_in_progress,
                      }"
                      >{{ remainingTimes[props.tournament.instance_id] }}</div
                    >
                  </div>

                  <button
                    class="p-[5px] md:p-[9px] rounded-md leading-[12px] text-[10px] md:text-[13px] w-full transition-transform duration-300 hover:scale-[1.01] font-extrabold"
                    :class="{
                      'bg-[#0808B3] text-white':
                        props.tournament.is_user_registered,
                      'bg-white text-[#0808B3]':
                        !props.tournament.is_user_registered,
                    }"
                    @click="
                      $router.push(
                        `/vip/tournaments/${props.tournament.instance_id}`,
                      )
                    "
                  >
                    {{ getTournamentStatus(props.tournament) }}
                  </button>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <pre
      v-if="isDev() && false"
      class="gamificationPreDebug"
    >
      {{ tournamentDetails }}
    </pre>
  </div>
</template>
